import React, { useState, useEffect } from 'react';
import {
  ShoppingBasket,
  Edit3
} from 'lucide-react';
import useModalBackButton from '../hooks/useModalBackButton';
import ItemSelectorModal from './ItemSelectorModal';
import ItemDetailModal from './ItemDetailModal';
import OrderServiceModal from './OrderServiceModal';
import DiscountSelectorModal from './DiscountSelectorModal';
import useAuthUserType from '../hooks/useAuthUserType';
import { canCurrentUserApplyDiscount } from '../utils/discountAccess';
import { haptic } from '../utils/vibration';
import { getServiceIconComponent } from '../utils/serviceIcons';

const CART_ICON_TONES = {
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-300'
};

function CartServiceIcon({ icon, tone = 'blue' }) {
  const Icon = getServiceIconComponent(icon);
  return (
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${CART_ICON_TONES[tone] || CART_ICON_TONES.blue}`}>
      <Icon className="h-4.5 w-4.5" />
    </span>
  );
}

/**
 * Компонент корзины заказа — предметы, услуги, подсчёт суммы
 * Используется на NewOrder.js и может использоваться на странице заказа
 */
const OrderCart = ({
  items = [],
  onItemsChange,
  onSave,
  clientType = 'b2c',
  serviceType = '',
  priceCoefficient = 1.0,
  priceAt = null,
  discountContext = {},
  onDiscountBlocked,
  className = ''
}) => {
  const [draftItems, setDraftItems] = useState(items);
  const [showItemSelectorModal, setShowItemSelectorModal] = useState(false);
  const [showOrderServiceModal, setShowOrderServiceModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const { userType: currentUserType, loaded: currentUserTypeLoaded } = useAuthUserType();

  const closeItemSelectorModal = useModalBackButton(showItemSelectorModal, () => setShowItemSelectorModal(false));
  const closeOrderServiceModal = useModalBackButton(showOrderServiceModal, () => setShowOrderServiceModal(false));
  const closeItemDetailModal = useModalBackButton(!!selectedItemForDetail, () => setSelectedItemForDetail(null));

  useEffect(() => {
    setDraftItems(items);
  }, [items]);

  const addItem = (item) => {
    // Не добавляем дубли для доп. услуг заказа
    if (item.isAdditional && item.additionalType === 'order' && item.service_id) {
      if (draftItems.find(i => i.isAdditional && i.additionalType === 'order' && i.service_id === item.service_id)) return;
    }
    const newItem = { ...item, id: Date.now() + Math.random() };
    haptic('success');
    setDraftItems(prev => [...prev, newItem]);
  };

  const addDiscount = (discount) => {
    // Не добавляем если уже есть
    if (draftItems.find(i => i.isAdditional && i.additionalType === 'discount' && i.discountId === discount.id)) return;
    haptic('success');
    setDraftItems(prev => [...prev, {
      id: Date.now() + Math.random(),
      name: discount.name,
      price: discount.discount_value,
      unitType: discount.discount_type === 'percent' ? 'percent' : 'rub',
      quantity: 1,
      unit: 'шт',
      isAdditional: true,
      additionalType: 'discount',
      discountId: discount.id,
      discountSnapshot: {
        id: discount.id,
        name: discount.name,
        discount_type: discount.discount_type,
        discount_value: discount.discount_value,
        system_code: discount.system_code || null,
        template_type: discount.template_type || null,
        client_type: discount.client_type || clientType,
        scope: discount.scope || 'order',
        service_id: discount.service_id || null,
        group_id: discount.group_id || null,
        service_type: discount.service_type || null,
        allowed_roles: discount.allowed_roles || 'all',
        min_order_amount: discount.min_order_amount ?? null,
        min_items_count: discount.min_items_count ?? null,
        max_discount_amount: discount.max_discount_amount ?? null
      }
    }]);
  };

  const removeDiscount = (discountId) => {
    haptic('tap');
    setDraftItems(prev => prev.filter(i => !(i.isAdditional && i.additionalType === 'discount' && i.discountId === discountId)));
  };

  const removeItem = (id) => {
    haptic('tap');
    setDraftItems(prev => prev.filter(i => i.id !== id));
  };

  const removeLastItemByServiceId = (serviceId) => {
    haptic('tap');
    setDraftItems((prev) => {
      for (let index = prev.length - 1; index >= 0; index -= 1) {
        const item = prev[index];
        if (!item?.isAdditional && item.service_id === serviceId) {
          const next = [...prev];
          next.splice(index, 1);
          return next;
        }
      }
      return prev;
    });
  };

  // Удаление доп. услуги заказа по service_id (используется в OrderServiceModal)
  const removeServiceItem = (serviceId) => {
    haptic('tap');
    setDraftItems(prev => prev.filter(i => !(i.isAdditional && i.additionalType === 'order' && i.service_id === serviceId)));
  };

  const updateItem = (updatedItem) => {
    haptic('selection');
    setDraftItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
  };

  const replaceItem = (id, replacement) => {
    haptic('selection');
    setDraftItems(prev => prev.map(item => (
      item.id === id
        ? {
            ...item,
            ...replacement
          }
        : item
    )));
  };

  const getAdjustedUnitPrice = (item) => {
    const basePrice = Number(item?.price || 0);
    const coeff = Number(priceCoefficient || 1);
    return basePrice * (coeff > 0 ? coeff : 1);
  };

  const getAdjustedRegularUnitPrice = (item) => {
    const basePrice = Number(item?.regular_price ?? item?.price ?? 0);
    const coeff = Number(priceCoefficient || 1);
    return basePrice * (coeff > 0 ? coeff : 1);
  };

  const handleSave = () => {
    haptic('success');
    onItemsChange?.(draftItems);
    onSave?.(draftItems);
  };

  // Подсчёт стоимости предмета
  const calculateItemCost = (item) => {
    if (!item) return { total: 0, known: true };

    const basePrice = item.price || 0;
    const coeff = priceCoefficient || 1.0;
    const adjustedPrice = basePrice * coeff; // цена с коэффициентом
    const quantity = item.quantity || 1;
    let known = true;

    if (item.unit === 'кг' && !item.weight) known = false;
    if ((item.unit === 'м²' || item.unit === 'м2') && !item.area) known = false;

    let baseCost = adjustedPrice * quantity;
    if (item.unit === 'кг' && item.weight) baseCost = adjustedPrice * parseFloat(item.weight);
    if ((item.unit === 'м²' || item.unit === 'м2') && item.area) baseCost = adjustedPrice * parseFloat(item.area);

    // Item-level percent services are based on the full item subtotal.
    let additionalCost = 0;
    for (const add of (item.additionalServices || [])) {
      const price = add.current_price || add.base_price || 0;
      const unitType = add.current_unit_type || 'rub';
      if (unitType === 'percent') {
        additionalCost += baseCost * (price / 100);
      } else {
        additionalCost += price;
      }
    }

    return { total: baseCost + additionalCost, known, baseCost, additionalCost };
  };

  // Итоговая сумма по формуле:
  // 1. базовая цена предметов с коэффициентом + доп услуги предметов
  // 2. + доп услуги заказа (% от п.1)
  // 3. - скидки (% от п.1+п.2)
  const getTotalPrice = () => {
    const hasUnknownItems = draftItems.some(i => !i.isAdditional && !calculateItemCost(i).known);

    // Шаг 1: предметы с коэффициентом + доп услуги предметов
    const itemsTotal = draftItems
      .filter(i => !i.isAdditional)
      .reduce((sum, item) => {
        const cost = calculateItemCost(item);
        return sum + (cost.known ? cost.total : 0);
      }, 0);

    // Шаг 2: доп услуги заказа
    const orderServicesTotal = draftItems
      .filter(i => i.isAdditional && i.additionalType !== 'discount')
      .reduce((sum, item) => {
        if (item.unitType === 'percent') return sum + itemsTotal * (item.price / 100);
        return sum + (item.price * (item.quantity || 1));
      }, 0);

    const subtotal = itemsTotal + orderServicesTotal;

    // Шаг 3: скидки от subtotal
    const discountTotal = draftItems
      .filter(i => i.isAdditional && i.additionalType === 'discount')
      .reduce((sum, item) => {
        if (item.unitType === 'percent') return sum + subtotal * (item.price / 100);
        return sum + item.price;
      }, 0);

    return {
      itemsTotal,
      orderServicesTotal,
      subtotal,
      discountTotal,
      total: subtotal - discountTotal,
      hasUnknown: hasUnknownItems
    };
  };

  const itemsCount = draftItems
    .filter(i => !i.isAdditional)
    .reduce((sum, item) => sum + Math.max(1, Number(item.quantity || 1)), 0);
  const { itemsTotal, orderServicesTotal, subtotal, discountTotal, total, hasUnknown } = getTotalPrice();
  const canEditDraftDiscount = (item) => (
    currentUserTypeLoaded && canCurrentUserApplyDiscount(item?.discountSnapshot, currentUserType)
  );

  return (
    <div className={`${className} flex flex-col`}>
      {/* Список предметов — скроллится */}
      <div className="flex-1 overflow-y-auto pb-2">
        {/* Предметы */}
        {draftItems.filter(i => !i.isAdditional).length > 0 && (
          <div className="mb-3">
            <div className="text-xs font-semibold text-blue-500 uppercase mb-2">Предметы</div>
            <div className="space-y-1">
              {draftItems.filter(i => !i.isAdditional).map((item, idx) => {
                const cost = calculateItemCost(item);
                const addCount = item.additionalServices?.length || 0;
                return (
                  <div key={item.id} className="flex flex-col px-3 py-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-blue-400 font-mono shrink-0">#{idx + 1}</span>
                          <CartServiceIcon icon={item.icon} tone="blue" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.unit === 'шт' ? `${item.quantity || 1} шт.`
                            : item.unit === 'кг' ? (item.weight ? `${item.weight} кг` : 'Кг. — не указан')
                            : (item.unit === 'м²' || item.unit === 'м2') ? (
                              item.area
                                ? (item.width && item.length ? `${item.area} м² (${item.width}×${item.length} м)` : `${item.area} м²`)
                                : 'м² — не указана'
                            )
                            : item.unit}
                          {item.promotion_price != null
                            ? ` • ${getAdjustedUnitPrice(item).toFixed(0)} ₽/${item.unit || 'шт'} вместо ${getAdjustedRegularUnitPrice(item).toFixed(0)} ₽`
                            : ` • ${getAdjustedUnitPrice(item).toFixed(0)} ₽/${item.unit || 'шт'}`}
                          {addCount > 0 && ` • ${addCount} доп.`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <span className={`text-sm font-medium mr-1 ${cost.known ? 'text-gray-900 dark:text-gray-100' : 'text-orange-500'}`}>
                          {cost.known ? `${cost.total.toFixed(0)} ₽` : '?'}
                        </span>
                        <button
                          onClick={() => {
                            haptic('open');
                            setSelectedItemForDetail(item);
                          }}
                          className="cart-btn w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center hover:bg-blue-200 transition-colors"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                        </button>
                        <button
                          onClick={() => {
                            haptic(confirmDeleteId === item.id ? 'close' : 'warning');
                            setConfirmDeleteId(confirmDeleteId === item.id ? null : item.id);
                          }}
                          className="cart-btn w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center hover:bg-red-200 transition-colors"
                        >
                          <span className="text-red-600 dark:text-red-400 text-sm leading-none">×</span>
                        </button>
                      </div>
                    </div>
                    {confirmDeleteId === item.id && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-1">Удалить предмет?</span>
                          <button
                            onClick={() => { removeItem(item.id); setConfirmDeleteId(null); }}
                            className="cart-btn px-3 h-6 rounded-full bg-red-500 text-white text-xs font-medium flex items-center"
                        >
                          Удалить
                        </button>
                          <button
                            onClick={() => {
                              haptic('close');
                              setConfirmDeleteId(null);
                            }}
                            className="cart-btn px-3 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium flex items-center"
                        >
                          Отмена
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Разделитель */}
        {draftItems.filter(i => i.isAdditional).length > 0 && draftItems.filter(i => !i.isAdditional).length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 my-3" />
        )}

        {/* Услуги заказа */}
        {draftItems.filter(i => i.isAdditional && i.additionalType === 'order').length > 0 && (
          <div className="mb-3">
            <div className="text-xs font-semibold text-purple-500 uppercase mb-2">Услуги заказа</div>
            <div className="space-y-1">
              {draftItems.filter(i => i.isAdditional && i.additionalType === 'order').map((item) => {
                const rubles = item.unitType === 'percent'
                  ? (hasUnknown ? null : (itemsTotal * item.price / 100))
                  : null;
                return (
                  <div key={item.id} className="flex items-center justify-between px-3 py-2 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <CartServiceIcon icon={item.icon} tone="purple" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</div>
                        <div className="text-xs text-purple-500">
                        {item.unitType === 'percent' ? `+${item.price}%` : `+${item.price} ₽`}
                        {rubles !== null && ` = +${rubles.toFixed(0)} ₽`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      <span className="text-sm font-medium text-purple-600 dark:text-purple-400 mr-1">
                        {rubles !== null ? `${rubles.toFixed(0)} ₽` : item.unitType === 'percent' ? `${item.price}%` : `${item.price} ₽`}
                      </span>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="cart-btn w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center hover:bg-red-200 transition-colors"
                      >
                        <span className="text-red-600 dark:text-red-400 text-sm leading-none">×</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Скидки */}
        {draftItems.filter(i => i.isAdditional && i.additionalType === 'discount').length > 0 && (
          <div className="mb-3">
            <div className="text-xs font-semibold text-orange-500 uppercase mb-2">Скидки</div>
            <div className="space-y-1">
              {draftItems.filter(i => i.isAdditional && i.additionalType === 'discount').map((item) => {
                const discountAmount = item.unitType === 'percent'
                  ? (hasUnknown ? null : (subtotal * item.price / 100))
                  : item.price;
                const canRemoveDiscountItem = canEditDraftDiscount(item);
                return (
                  <div key={item.id} className="flex items-center justify-between px-3 py-2 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</div>
                      <div className="text-xs text-orange-500">
                        {item.unitType === 'percent' ? `-${item.price}%` : `-${item.price} ₽`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      <span className="text-sm font-medium text-orange-600 dark:text-orange-400 mr-1">
                        {discountAmount !== null ? `-${discountAmount.toFixed(0)} ₽` : '?'}
                      </span>
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={!canRemoveDiscountItem}
                        className={`cart-btn flex h-7 w-7 items-center justify-center rounded-full text-sm leading-none transition-colors ${
                          canRemoveDiscountItem
                            ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                        }`}
                      >
                        <span>×</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {draftItems.length === 0 && (
          <div className="flex min-h-[42vh] flex-col items-center justify-center px-6 py-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-300">
              <ShoppingBasket className="h-8 w-8" />
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Корзина пока пуста</div>
            <div className="mt-2 max-w-[260px] text-sm leading-5 text-gray-500 dark:text-gray-400">
              Добавьте предмет или услугу, чтобы продолжить оформление заказа.
            </div>
          </div>
        )}
      </div>

      {/* Фиксированный блок внизу — прилеплен к краям */}
      <div className="fixed left-3 right-3 z-10" style={{ bottom: 'max(0.75rem, max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)))' }}>
        <div className="mx-auto max-w-[720px] rounded-2xl border border-gray-200 bg-white/95 px-4 pt-3 pb-4 shadow-lg ring-1 ring-black/5 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 dark:ring-white/10">
          {/* Итого */}
          {draftItems.length > 0 && (
            <div className="mb-3 space-y-1">
              <div className="flex justify-between text-gray-600 dark:text-gray-400 text-xs">
                <span>Предметы</span>
                <span>{hasUnknown ? '? ' : ''}{itemsTotal.toFixed(0)} ₽</span>
              </div>
              {orderServicesTotal > 0 && (
                <div className="flex justify-between text-purple-500 text-xs">
                  <span>Доп. услуги заказа</span>
                  <span>+{orderServicesTotal.toFixed(0)} ₽</span>
                </div>
              )}
              {discountTotal > 0 && (
                <div className="flex justify-between text-orange-500 text-xs">
                  <span>Скидки</span>
                  <span>-{discountTotal.toFixed(0)} ₽</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-1 text-sm font-bold text-gray-900 dark:border-gray-700 dark:text-gray-100">
                <span>Итого</span>
                <span>{hasUnknown ? '? ' : ''}{total.toFixed(0)} ₽</span>
              </div>
            </div>
          )}

          {/* Кнопки */}
          <div className="grid grid-cols-3 gap-2">
          <button onClick={() => { haptic('open'); setShowItemSelectorModal(true); }} className="py-2 rounded-lg font-medium border-2 border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 transition-all text-xs">Предметы</button>
          <button onClick={() => { haptic('open'); setShowOrderServiceModal(true); }} className="py-2 rounded-lg font-medium border-2 border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900 transition-all text-xs">Услуги</button>
          <button onClick={() => { haptic('open'); setShowDiscountModal(true); }} className="py-2 rounded-lg font-medium border-2 border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900 transition-all text-xs">Скидки</button>
          </div>
          <button
            onClick={handleSave}
            className="mt-2 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            Сохранить
          </button>
        </div>
      </div>

      {/* Модалки */}
      <ItemSelectorModal
        open={showItemSelectorModal}
        onClose={closeItemSelectorModal}
        onAdd={addItem}
        onRemoveLast={removeLastItemByServiceId}
        onReplace={replaceItem}
        clientType={clientType}
        serviceType={serviceType}
        existingItems={draftItems}
        priceAt={priceAt}
        priceCoefficient={priceCoefficient}
      />
      <ItemDetailModal
        open={!!selectedItemForDetail}
        onClose={closeItemDetailModal}
        item={selectedItemForDetail}
        itemIndex={selectedItemForDetail ? draftItems.filter(i => !i.isAdditional).findIndex(i => i.id === selectedItemForDetail.id) : null}
        serviceType={serviceType}
        clientType={clientType}
        onItemUpdate={updateItem}
        priceAt={priceAt}
        priceCoefficient={priceCoefficient}
      />
      <OrderServiceModal
        open={showOrderServiceModal}
        onClose={closeOrderServiceModal}
        onAdd={addItem}
        onRemove={removeServiceItem}
        clientType={clientType}
        existingItems={draftItems}
        priceAt={priceAt}
      />
      <DiscountSelectorModal
        open={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        onSelect={addDiscount}
        onRemove={removeDiscount}
        clientType={clientType}
        existingItems={draftItems}
        orderContext={{
          ...discountContext,
          itemsCount
        }}
        currentUserType={currentUserType}
        currentUserTypeLoaded={currentUserTypeLoaded}
        onBlocked={onDiscountBlocked}
      />
    </div>
  );
};

export default OrderCart;
